import currencyService from '../services/currencyService';
import { CURRENCIES } from '../constants/hrConstants';

/**
 * Currency utility functions that work with dynamic currencies
 */

// Cache for currency options to avoid repeated API calls
let currencyOptionsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get currency options from API with fallback to static list
 * This replaces the static CURRENCIES array usage
 */
export const getDynamicCurrencyOptions = async () => {
  try {
    // Check cache first
    if (currencyOptionsCache && cacheTimestamp && 
        (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      console.log('🚀 Using cached currency options:', currencyOptionsCache);
      return currencyOptionsCache;
    }

    console.log('🔄 Fetching fresh currency options from API...');
    // Fetch from API
    const options = await currencyService.getCurrencyOptions();
    
    console.log('📋 Raw options received from currencyService:', options);
    
    // Ensure we have a valid array
    const validOptions = Array.isArray(options) ? options.filter((option, index) => {
      const isValid = option && 
        option.value !== undefined && option.value !== null && option.value !== '' &&
        option.label !== undefined && option.label !== null && option.label !== '';
      if (!isValid) {
        console.warn(`⚠️ [dynamicCurrencyUtils] Filtering out invalid option at index ${index}:`, {
          option,
          hasOption: !!option,
          hasValue: option?.value !== undefined,
          hasLabel: option?.label !== undefined,
          valueType: typeof option?.value,
          labelType: typeof option?.label
        });
      }
      return isValid;
    }) : [];
    
    console.log('✅ Valid options after filtering:', validOptions);
    
    // Update cache only if we got valid options
    if (validOptions.length > 0) {
      currencyOptionsCache = validOptions;
      cacheTimestamp = Date.now();
      console.log('💾 Updated currency options cache');
      return validOptions;
    } else {
      console.warn('⚠️ No valid options from API, returning empty array');
      // If no valid options from API, return empty array
      return [];
    }
  } catch (error) {
    console.error('❌ Failed to fetch dynamic currencies:', error);
    // Return empty array instead of fallback
    return [];
  }
};

/**
 * Get currency symbol by code
 */
export const getCurrencySymbolByCode = async (code) => {
  try {
    const options = await getDynamicCurrencyOptions();
    const currency = options.find(c => c.value === code);
    return currency ? currency.symbol : code;
  } catch (error) {
    console.error('Failed to get currency symbol:', error);
    // Fallback to code if symbol not found
    return code;
  }
};

/**
 * Validate if a currency code is valid
 */
export const isValidCurrencyCode = async (code) => {
  try {
    const options = await getDynamicCurrencyOptions();
    return options.some(c => c.value === code);
  } catch (error) {
    console.error('Failed to validate currency code:', error);
    // Fallback to checking against static list
    return CURRENCIES.some(c => c.value === code);
  }
};

/**
 * Get currency details by code
 */
export const getCurrencyDetails = async (code) => {
  try {
    const currency = await currencyService.getCurrencyByCode(code);
    return currency;
  } catch (error) {
    console.error('Failed to get currency details:', error);
    // Fallback to static currency info
    const staticCurrency = CURRENCIES.find(c => c.value === code);
    if (staticCurrency) {
      return {
        code: staticCurrency.value,
        name: staticCurrency.label.split(' - ')[1],
        symbol: staticCurrency.symbol || code,
        isRTL: ['SAR', 'QAR', 'AED', 'KWD', 'BHD', 'OMR', 'EGP'].includes(code)
      };
    }
    return null;
  }
};

/**
 * Clear the currency cache
 */
export const clearCurrencyCache = () => {
  currencyOptionsCache = null;
  cacheTimestamp = null;
  if (currencyService && currencyService.clearCache) {
    currencyService.clearCache();
  }
};

/**
 * Force clear cache and reload currencies
 */
export const forceReloadCurrencies = async () => {
  console.log('🔄 Force reloading currencies...');
  clearCurrencyCache();
  return await getDynamicCurrencyOptions();
};

/**
 * Format amount with currency
 */
export const formatAmountWithCurrency = async (amount, currencyCode, locale = 'en-US') => {
  try {
    const currency = await getCurrencyDetails(currencyCode);
    if (currency && currency.formatAmount) {
      return currency.formatAmount(amount);
    }
    
    // Fallback to Intl.NumberFormat
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  } catch (error) {
    console.error('Failed to format currency:', error);
    // Basic fallback
    const symbol = await getCurrencySymbolByCode(currencyCode);
    return `${symbol} ${amount.toFixed(2)}`;
  }
};

/**
 * Convert static currency usage to dynamic
 * This is a helper for migration
 */
export const migrateToDynamicCurrency = (Component) => {
  return class DynamicCurrencyComponent extends Component {
    constructor(props) {
      super(props);
      this.state = {
        ...this.state,
        currencyOptions: CURRENCIES, // Start with static as fallback
        currencyLoading: true
      };
    }

    async componentDidMount() {
      super.componentDidMount && super.componentDidMount();
      
      try {
        const options = await getDynamicCurrencyOptions();
        this.setState({ 
          currencyOptions: options,
          currencyLoading: false 
        });
      } catch (error) {
        console.error('Failed to load dynamic currencies:', error);
        this.setState({ currencyLoading: false });
      }
    }
  };
};